import { Link } from '@tanstack/react-router';
import { Pencil, ExternalLink, Loader2, CalendarClock } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  type DiffusionRow as Row,
  DIFFUSION_TYPE_LABELS,
  DIFFUSION_PLATFORM_LABELS,
  DIFFUSION_STATUS_LABELS,
  isActionable,
} from '@jeraisonne/db/diffusions';
import { formatDeadlineLong } from '@/lib/student-objectives/deadline-format';
import { statusBadgeClasses, platformDotClasses } from './diffusion-badges';

/**
 * Gabarit de colonnes partagé en-tête ⇄ lignes.
 * Mobile (3 col) : case · contenu · modifier.
 * sm+ (5 col)    : case · contenu · date/heure · voir · modifier.
 */
const GRID =
  'grid grid-cols-[1.75rem_minmax(0,1fr)_auto] sm:grid-cols-[1.75rem_minmax(0,1fr)_11.5rem_4.5rem_2.5rem] items-center gap-x-3';

interface HeaderProps {
  allSelected: boolean;
  selectableCount: number;
  onToggleAll: () => void;
}

/** En-tête de tableau (colonnes alignées sur les lignes). */
export function DiffusionsTableHeader({ allSelected, selectableCount, onToggleAll }: HeaderProps) {
  return (
    <div className={cn(GRID, 'border-ink-200 mb-2 border-b px-3 pb-2')}>
      <input
        type="checkbox"
        checked={allSelected}
        onChange={onToggleAll}
        disabled={selectableCount === 0}
        aria-label="Tout sélectionner sur cette page"
        className="text-primary-600 focus:ring-primary-500/40 border-ink-300 h-5 w-5 rounded disabled:opacity-30"
      />
      <span className="font-display text-ink-500 text-[11px] font-bold uppercase tracking-wide">
        Diffusion
      </span>
      <span className="font-display text-ink-500 hidden text-[11px] font-bold uppercase tracking-wide sm:block">
        Date / heure
      </span>
      <span className="font-display text-ink-500 hidden text-[11px] font-bold uppercase tracking-wide sm:block">
        Lien
      </span>
      <span className="hidden sm:block" aria-hidden />
    </div>
  );
}

interface DiffusionRowProps {
  row: Row;
  selected: boolean;
  onToggle: (row: Row) => void;
}

export function DiffusionRowItem({ row, selected, onToggle }: DiffusionRowProps) {
  const selectable = isActionable(row.status);
  const isScheduled = row.status === 'scheduled' && !!row.scheduled_at;
  const dateIso = isScheduled ? row.scheduled_at : row.updated_at;

  const dateCell = (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-sans text-[12px]',
        isScheduled ? 'text-primary-600 font-semibold' : 'text-ink-400',
      )}
    >
      {isScheduled && <CalendarClock className="h-3.5 w-3.5 shrink-0" />}
      {formatDeadlineLong(dateIso) || '—'}
    </span>
  );

  const seeCell = row.external_url ? (
    <a
      href={row.external_url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 font-sans text-[12px] text-emerald-600 hover:underline"
    >
      <ExternalLink className="h-3.5 w-3.5" /> Voir
    </a>
  ) : (
    <span className="text-ink-300 font-sans text-[12px]">—</span>
  );

  return (
    <div
      className={cn(
        GRID,
        'border-ink-200 rounded-2xl border bg-white px-3 py-2.5 transition-colors',
        selected && 'border-primary-300 bg-primary-50/40',
      )}
    >
      {/* Col 1 — case à cocher */}
      <input
        type="checkbox"
        checked={selected}
        disabled={!selectable}
        onChange={() => onToggle(row)}
        aria-label={`Sélectionner ${row.title}`}
        className="text-primary-600 focus:ring-primary-500/40 border-ink-300 h-5 w-5 rounded disabled:opacity-30"
      />

      {/* Col 2 — contenu */}
      <div className="min-w-0">
        <div className="mb-1 flex flex-wrap items-center gap-1.5">
          <span className="bg-ink-100 text-ink-600 rounded-full px-2 py-0.5 text-[11px] font-bold">
            {DIFFUSION_TYPE_LABELS[row.diffusion_type]}
          </span>
          <span className="bg-ink-50 text-ink-700 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold">
            <span className={cn('h-2 w-2 rounded-full', platformDotClasses(row.platform))} />
            {DIFFUSION_PLATFORM_LABELS[row.platform]}
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold',
              statusBadgeClasses(row.status),
            )}
          >
            {row.status === 'publishing' && <Loader2 className="h-3 w-3 animate-spin" />}
            {DIFFUSION_STATUS_LABELS[row.status]}
          </span>
        </div>

        <h3 className="font-display text-ink-900 line-clamp-1 text-[15px] font-extrabold">
          {row.title}
        </h3>

        {/* Mobile : date + voir + erreur sous le titre (les colonnes sm+ sont masquées) */}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 sm:hidden">
          {dateCell}
          {row.external_url && seeCell}
        </div>
        {row.status === 'failed' && row.error && (
          <p className="mt-0.5 line-clamp-1 font-sans text-[11px] text-red-500" title={row.error}>
            {row.error}
          </p>
        )}
      </div>

      {/* Col 3 — date / heure (sm+) */}
      <div className="hidden min-w-0 sm:block">{dateCell}</div>

      {/* Col 4 — lien « Voir » (sm+) */}
      <div className="hidden sm:block">{seeCell}</div>

      {/* Col 5 — lien « Modifier » */}
      <Link
        to={row.edit_route as never}
        className="text-ink-400 hover:text-primary-600 hover:bg-primary-50 flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors"
        aria-label="Modifier"
        title="Modifier"
      >
        <Pencil className="h-4 w-4" />
      </Link>
    </div>
  );
}
